// Agrupación y búsqueda de las herramientas del panel admin.
//
// Fuente de los datos: GET /api/admin/tool-catalog. Cada herramienta viene con
// `group`, `group_label`, `group_order` y `keywords` ya resueltos por el backend
// (ver ADMIN_TOOL_GROUPS en backend/auth.py), así que acá no hay ningún mapa de
// nombres que mantener en paralelo: sólo el cómo se ordena y se filtra.
//
// Lo usan el menú lateral (AdminLayout) y los chips de permisos
// (AdminUsersPage) para que agrupador y buscador se comporten igual en los dos
// lados.

const GRUPO_FALLBACK = { key: 'otras', label: 'Otras herramientas', order: 90 };

/**
 * Texto comparable: sin acentos, sin mayúsculas, sin espacios de más.
 * Nadie escribe tildes cuando filtra, así que "aprobacion" tiene que encontrar
 * "Aprobación de Cotizaciones".
 */
export const normalizar = (texto) =>
  (texto ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // marcas diacríticas
    .toLowerCase()
    .trim();

/**
 * ¿La herramienta coincide con la búsqueda? Se busca en el nombre, en la
 * descripción y en las keywords (sinónimos que no están en el texto visible:
 * "AFIP" para Conciliación ARCA, "cheque" para Cobranzas).
 *
 * Con varias palabras se exigen todas: "factura pdf" sólo trae las que tengan
 * las dos, que es lo que espera quien escribe de más para achicar la lista.
 */
export const coincide = (tool, consulta) => {
  const terminos = normalizar(consulta).split(/\s+/).filter(Boolean);
  if (terminos.length === 0) return true;

  const heno = normalizar(
    [tool.label, tool.description, tool.key, ...(tool.keywords || [])].join(' ')
  );
  return terminos.every((termino) => heno.includes(termino));
};

/** Filtra una lista de herramientas por la búsqueda. */
export const filtrarTools = (tools, consulta) =>
  (tools || []).filter((tool) => coincide(tool, consulta));

/**
 * Agrupa las herramientas respetando el orden que ya trae el catálogo.
 * Devuelve [{ key, label, order, tools }]. `label` en null = grupo sin
 * encabezado (se dibuja suelto arriba de todo).
 *
 * Un grupo sin herramientas visibles no aparece: si el usuario sólo tiene
 * Cobranzas, no tiene que ver tres títulos vacíos.
 */
export const agruparTools = (tools) => {
  const grupos = new Map();

  (tools || []).forEach((tool) => {
    const key = tool.group || GRUPO_FALLBACK.key;
    if (!grupos.has(key)) {
      grupos.set(key, {
        key,
        // group_label puede ser null a propósito (grupo "destacado"): sólo se
        // cae al fallback cuando la propiedad no vino del backend.
        label: tool.group_label !== undefined ? tool.group_label : GRUPO_FALLBACK.label,
        order: tool.group_order ?? GRUPO_FALLBACK.order,
        tools: [],
      });
    }
    grupos.get(key).tools.push(tool);
  });

  return [...grupos.values()].sort((a, b) => a.order - b.order);
};
